import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { UNIT_CODES } from "@/lib/romanianCounties";
import { cn } from "@/lib/utils";
import type { LineItem } from "./types";

interface LineItemsEditorProps {
  items: LineItem[];
  onAddItem: () => void;
  onRemoveItem: (id: string) => void;
  onUpdateItem: (id: string, field: keyof LineItem, value: string | number) => void;
  /** Field IDs flagged invalid (e.g. "items[2].quantity" or "items"). */
  invalidFields?: Set<string>;
}

const errorClass = "border-destructive focus-visible:ring-destructive";

const LineItemsEditor = ({ items, onAddItem, onRemoveItem, onUpdateItem, invalidFields }: LineItemsEditorProps) => {
  const isInvalid = (field: string) => invalidFields?.has(field) ?? false;
  const itemsLevelInvalid = isInvalid("items");

  return (
    <div className="space-y-4" data-field-group="items">
      <div className="flex items-center justify-between">
        <h3 className={cn("font-heading font-semibold", itemsLevelInvalid && "text-destructive")}>
          Produse / Servicii
        </h3>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="outline" size="sm" onClick={onAddItem} data-invalid-field="items">
              <Plus className="h-4 w-4 mr-1" /> Adaugă
            </Button>
          </TooltipTrigger>
          <TooltipContent>Adaugă un produs sau serviciu nou</TooltipContent>
        </Tooltip>
      </div>
      {items.map((item, idx) => {
        const namePath = `items[${idx}].item_name`;
        const qtyPath = `items[${idx}].quantity`;
        const pricePath = `items[${idx}].unit_price`;
        const vatPath = `items[${idx}].vat_rate`;
        const unitPath = `items[${idx}].unit_code`;
        return (
          <div key={item.id} className="rounded-xl border border-border bg-card p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">#{idx + 1}</span>
              {items.length > 1 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-11 w-11 sm:h-8 sm:w-8" onClick={() => onRemoveItem(item.id)} aria-label="Șterge linia">
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Șterge acest produs</TooltipContent>
                </Tooltip>
              )}
            </div>
            <div className="space-y-2">
              <Label>Denumire</Label>
              <Input
                data-invalid-field={namePath}
                aria-invalid={isInvalid(namePath)}
                placeholder="ex: Servicii consultanță"
                value={item.item_name}
                onChange={(e) => onUpdateItem(item.id, "item_name", e.target.value)}
                className={cn(isInvalid(namePath) && errorClass)}
              />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="space-y-2">
                <Label>Cantitate</Label>
                <Input
                  data-invalid-field={qtyPath}
                  aria-invalid={isInvalid(qtyPath)}
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.01"
                  placeholder="1"
                  value={item.quantity || ""}
                  onChange={(e) => onUpdateItem(item.id, "quantity", e.target.value === "" ? 0 : parseFloat(e.target.value) || 0)}
                  className={cn(isInvalid(qtyPath) && errorClass)}
                />
              </div>
              <div className="space-y-2" data-field-group={unitPath}>
                <Label>U.M.</Label>
                <Select value={item.unit_code} onValueChange={(v) => onUpdateItem(item.id, "unit_code", v)}>
                  <SelectTrigger
                    data-invalid-field={unitPath}
                    aria-invalid={isInvalid(unitPath)}
                    className={cn(isInvalid(unitPath) && errorClass)}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UNIT_CODES.map((u) => (
                      <SelectItem key={u.code} value={u.code}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Preț Unitar</Label>
                <Input
                  data-invalid-field={pricePath}
                  aria-invalid={isInvalid(pricePath)}
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.01"
                  placeholder="0"
                  value={item.unit_price || ""}
                  onChange={(e) => onUpdateItem(item.id, "unit_price", e.target.value === "" ? 0 : parseFloat(e.target.value) || 0)}
                  className={cn(isInvalid(pricePath) && errorClass)}
                />
              </div>
              <div className="space-y-2">
                <Label>TVA %</Label>
                <Input
                  data-invalid-field={vatPath}
                  aria-invalid={isInvalid(vatPath)}
                  type="number"
                  inputMode="decimal"
                  min="0"
                  max="100"
                  value={item.vat_rate}
                  onChange={(e) => onUpdateItem(item.id, "vat_rate", parseFloat(e.target.value) || 0)}
                  className={cn(isInvalid(vatPath) && errorClass)}
                />
              </div>
            </div>
            <div className="text-right text-sm text-muted-foreground">
              Subtotal: {(item.quantity * item.unit_price).toLocaleString("ro-RO", { style: "currency", currency: "RON" })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default LineItemsEditor;
